import axios from 'axios';

// ⚠️ IMPORTANT: DO NOT CHANGE THIS TO HARDCODED URL IN DEVELOPMENT!
// This MUST use '/api' in development to work with the proxy and avoid CORS errors.
// The proxy (setupProxy.js) forwards /api requests to https://securereservation.org/hotels/api
// Use proxy in development (localhost), full URL in production
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'https://securereservation.org/hotels/api'  // Proxy will forward to https://securereservation.org/hotels/api
  : 'https://securereservation.org/hotels/api';

/**
 * Fetch addon list from the API
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.hotel_id - Hotel accommodation ID (REQUIRED)
 * @param {number} params.lang_id - Language ID (optional, defaults to 1)
 * @param {string} params.currency - Currency code (optional, defaults to 'INR')
 * @returns {Promise<Array>} Array of addon data
 */
export const fetchAddonList = async (params = {}) => {
  try {
    // Extract parameters
    const hotel_id = params.hotel_id || params.accommodationid;
    const lang_id = params.lang_id || 1;
    const currency = params.currency || '';

    // Validate required parameters
    if (!hotel_id) {
      throw new Error('hotel_id is required to fetch addon list');
    }

    // Build query parameters
    const queryParams = new URLSearchParams({
      hotel_id: String(hotel_id),
      lang_id: String(lang_id),
      currency: currency,
    });

    // Make API request
    const response = await axios.get(`${API_BASE_URL}/addonlist?${queryParams.toString()}`, {
      timeout: 10000, // 10 second timeout
      headers: {
        'Accept': 'application/json',
      },
    });

    // Check if response has data
    if (!response.data || !response.data.Result) {
      return [];
    }

    return response.data;
  } catch (error) {
    if (error.response) {
      // Server responded with error status
      throw new Error(`Failed to fetch addon list: ${error.response.status} ${error.response.statusText}`);
    } else if (error.request) {
      // Request made but no response received
      throw new Error('No response received from addon API. Please check your connection.');
    } else {
      // Error setting up request
      throw new Error(`Error fetching addon list: ${error.message}`);
    }
  }
};

/**
 * Transform API addon response to local format
 * 
 * @param {Object} apiResponse - API response from fetchAddonList
 * @returns {Array} Array of transformed addon objects
 */
export const transformApiAddonsToLocalFormat = (apiResponse) => {
  try {
    if (!apiResponse || !apiResponse.Result) {
      return [];
    }

    const result = apiResponse.Result;
    const addons = [];

    // Handle single activity or array of activities
    let activities = [];
    if (result.Activity) {
      if (Array.isArray(result.Activity)) {
        activities = result.Activity;
      } else {
        activities = [result.Activity];
      }
    }

    // Track used IDs to ensure uniqueness
    const usedIds = new Set();
    
    // Transform each activity to local format
    activities.forEach((activity, index) => {
      try {
        // --- Variations: API sends Variations.Variation as EITHER a single object
        // (Standard add-on, one price) OR an array (Per Day/Per Person, multiple options
        // like Guest 1/2/3 each with its own price). Normalise to an array. ---
        const rawVariation = activity.Variations?.Variation;
        const variationArr = Array.isArray(rawVariation)
          ? rawVariation
          : (rawVariation ? [rawVariation] : []);

        const variations = variationArr
          .map((v) => ({
            variationId: v?.VariationId != null ? String(v.VariationId) : '',
            value: v?.Value != null ? String(v.Value) : '',        // e.g. "Guest 1" ("0" = no label)
            price: parseFloat(v?.Price || '0') || 0,
          }))
          .filter((v) => v.variationId !== '');

        // A real variation SELECTOR only when there are 2+ options with meaningful labels.
        const hasVariations = variations.length > 1;
        // Default = first variation (UI can change it when hasVariations).
        const defaultVariation = variations[0] || { variationId: String(activity.ActivityId || index + 1), value: '', price: 0 };
        const variationId = defaultVariation.variationId;
        const price = defaultVariation.price;

        // Get image URL
        const imageUrl = activity.ImageUrl || '';

        // Get description (prefer ShortDescription, fallback to LongDescription)
        let description = activity.ShortDescription || '';
        if (!description && activity.LongDescription) {
          if (Array.isArray(activity.LongDescription)) {
            description = activity.LongDescription.join(' ');
          } else {
            description = activity.LongDescription;
          }
        }

        // Determine category from ActivityType or ActivityName
        let category = 'Services';
        if (activity.ActivityType) {
          category = activity.ActivityType;
        } else if (activity.ActivityName) {
          // Try to extract category from name
          if (activity.ActivityName.toLowerCase().includes('spa')) {
            category = 'Spa & Wellness';
          } else if (activity.ActivityName.toLowerCase().includes('transfer') || activity.ActivityName.toLowerCase().includes('airport')) {
            category = 'Transportation';
          } else if (activity.ActivityName.toLowerCase().includes('beach') || activity.ActivityName.toLowerCase().includes('cabana')) {
            category = 'Beach Services';
          }
        }

        // Determine duration (try to extract from description or use default)
        let duration = '60 minutes';
        if (description) {
          const durationMatch = description.match(/(\d+)\s*(?:minute|min|hour|hr|h)/i);
          if (durationMatch) {
            const value = parseInt(durationMatch[1]);
            if (description.toLowerCase().includes('hour') || description.toLowerCase().includes('hr') || description.toLowerCase().includes('h')) {
              duration = value === 1 ? '1 hour' : `${value} hours`;
            } else {
              duration = `${value} minutes`;
            }
          } else if (description.toLowerCase().includes('full day')) {
            duration = 'Full day';
          }
        }

        // Ensure unique ID
        let addonId = parseInt(activity.ActivityId) || null;
        if (!addonId || usedIds.has(addonId)) {
          // Generate unique ID if missing or duplicate
          let newId = index + 1;
          while (usedIds.has(newId)) {
            newId += 1000; // Add offset to avoid conflicts
          }
          addonId = newId;
        }
        usedIds.add(addonId);

        const transformedAddon = {
          id: addonId,
          name: activity.ActivityName || `Addon ${index + 1}`,
          image: imageUrl,
          description: description || 'No description available',
          price: price,
          duration: duration,
          category: category,
          // Additional API fields
          activityId: activity.ActivityId,
          variationId: variationId,          // default/selected variation id
          variations: variations,            // [{variationId, value, price}] — all options
          hasVariations: hasVariations,      // true when 2+ options → show a selector
          maxQty: parseInt(activity.MaxQty) || 5,
          currencyCode: activity.CurrencyCode || '',
          cancellationPolicy: activity.CancellationPolicy || '',
          guaranteePolicy: activity.GuaranteePolicy || '',
          activityType: activity.ActivityType || 'Standard',
          bookableWithRateplan: activity.BookableWithRateplan === '1',
          bookableIndividual: activity.BookableIndividual === '1',
        };

        addons.push(transformedAddon);
      } catch (error) {
        // Continue with next addon
      }
    });

    return addons;
  } catch (error) {
    return [];
  }
};

/**
 * Fetch and transform addon list in one call
 * 
 * @param {Object} params - Query parameters (same as fetchAddonList)
 * @returns {Promise<Array>} Array of transformed addon objects
 */
export const getAddonList = async (params = {}) => {
  try {
    const apiResponse = await fetchAddonList(params);
    return transformApiAddonsToLocalFormat(apiResponse);
  } catch (error) {
    throw error;
  }
};


/**
 * Type-multiplier for an add-on based on its ActivityType:
 *   Standard        -> 1
 *   "Per Day"/Night -> nights
 *   "Per Person"    -> persons (guests)
 * READ-ONLY, crash-safe. Caller multiplies by the chosen quantity separately.
 */
export const getAddonTypeMultiplier = (activityType, { nights = 1, persons = 1 } = {}) => {
  try {
    const t = String(activityType || '').toLowerCase();
    const n = Math.max(1, parseInt(nights) || 1);
    const p = Math.max(1, parseInt(persons) || 1);
    if (t.includes('day') || t.includes('night')) return n;
    if (t.includes('person') || t.includes('guest') || t.includes('pax')) return p;
    return 1; // Standard / unknown
  } catch (e) {
    return 1;
  }
};

/**
 * Resolve the unit price for an add-on, honouring the chosen variation.
 * If a variationId is given and matches one of addon.variations, use that price;
 * otherwise fall back to addon.price (the default variation). Crash-safe.
 */
export const getAddonUnitPrice = (addon, variationId) => {
  try {
    if (!addon) return 0;
    if (variationId != null && Array.isArray(addon.variations)) {
      const v = addon.variations.find((x) => x && String(x.variationId) === String(variationId));
      if (v && Number(v.price) >= 0) return Number(v.price) || 0;
    }
    return Number(addon.price) || 0;
  } catch (e) {
    return 0;
  }
};

/**
 * Line total for one add-on: unitPrice(variation) × quantity × type-multiplier.
 * Pass { quantity, nights, persons, variationId }. Crash-safe, never throws.
 */
export const getAddonLineTotal = (addon, { quantity = 1, nights = 1, persons = 1, variationId } = {}) => {
  try {
    if (!addon) return 0;
    const unit = getAddonUnitPrice(addon, variationId);
    const qty = Math.max(0, parseInt(quantity) || 0);
    const mult = getAddonTypeMultiplier(addon.activityType, { nights, persons });
    return unit * qty * mult;
  } catch (e) {
    return 0;
  }
};
